import type { SanityPageBuilderMediaGridContentImageCard, SanityPageBuilderMediaGridContentRow, SanityPageBuilderOusterMediaGridSection, SanityPageBuilderSection } from "@ouster/shared";
import { MediaGridBlockView } from "~/features/blocks/inventory/media-grid";
import { MediaGridContentImageCardView, type MediaGridContentImageCardViewProps } from "~/features/blocks/inventory/media-grid/content-image-card";
import { MediaGridContentRowView, type RowLayout } from "~/features/blocks/inventory/media-grid/content-row";
import { toCraftLinkArray } from "~/features/sanity/link";
import { hasRenderableSanityMedia, SanityMediaFigure } from "~/features/sanity/page-builder/sections/media-figure";
import { SanityRichText } from "~/features/sanity/rich-text";
import { takeFirst } from "~/features/app/utils";
type Props = {
  section: SanityPageBuilderSection & {
    loaded?: boolean;
    sectionContent: SanityPageBuilderOusterMediaGridSection;
  };
};
type MediaGridViewCard = Omit<MediaGridContentImageCardViewProps, "colSpan" | "featured"> & Pick<SanityPageBuilderMediaGridContentImageCard, "_key">;
type MediaGridViewRow = {
  _key?: SanityPageBuilderMediaGridContentRow["_key"];
  cards: MediaGridViewCard[];
  layout: RowLayout;
};
export function SanityOusterMediaGridSection({
  section
}: Props) {
  const {
    sectionContent,
    sectionSettings
  } = section;
  const rows = sectionContent.rows?.map<MediaGridViewRow>(row => ({
    _key: row._key,
    cards: row.cards?.map<MediaGridViewCard>(card => ({
      _key: card._key,
      description: card.description?.length ? <SanityRichText value={card.description} /> : undefined,
      link: takeFirst(toCraftLinkArray(card.link)) ?? undefined,
      media: hasRenderableSanityMedia(card.media) ? () => <SanityMediaFigure media={card.media} fit="cover" /> : undefined,
      supportLink: takeFirst(toCraftLinkArray(card.supportLink)) ?? undefined,
      title: card.title
    })) ?? [],
    layout: row.contentRowLayout ?? "auto"
  })) ?? [];
  return <MediaGridBlockView anchor={{
    id: sectionSettings?.sectionId?.current || sectionSettings?.anchorName || section._key,
    name: sectionSettings?.anchorName
  }} getRowKey={(row, index) => row._key || index} hasSolidBackground={sectionContent.hasSolidBackground} hasTopDivider={sectionSettings?.hasTopDivider ?? true} isHighlighted={sectionContent.isHighlighted} mode={sectionSettings?.mode || "light"} renderRow={row => <MediaGridContentRowView cards={row.cards} getCardKey={(card, placement) => card._key || placement.cardIndex} layout={row.layout} renderCard={(card, placement) => <MediaGridContentImageCardView colSpan={placement.colSpan} description={card.description} featured={placement.featured} link={card.link} media={card.media} supportLink={card.supportLink} title={card.title} />} />} rows={rows} sectionProps={{
    "data-sanity-section-key": section._key,
    "data-sanity-section-type": section._type
  }} theme={sectionSettings?.theme || "white"} />;
}